import { useEffect, useMemo, useState } from 'react';

import { parseFromKebabCase, parseToKebabCase } from '../../../helpers';
import { useQuery } from '../../../hooks';

export function usePaginatedSlider(children, preludePage) {
  const { updateSearchParams, getSearchParams } = useQuery();

  const hasPrelude = children.some(child => child.props.title === preludePage);
  const validKeys = children.map(child => child.props.title).filter(Boolean);
  const paramsPageKey = getSearchParams('step') ?? preludePage;
  const parsedParamsKey = parseFromKebabCase(paramsPageKey);
  const validKeysIndex = validKeys.indexOf(parsedParamsKey);

  const [currentPageKey, setCurrentPageKey] = useState(() => {
    if (hasPrelude && parsedParamsKey === preludePage) {
      return preludePage;
    }

    return validKeys[validKeysIndex];
  });

  /**
   * This useEffect helps us prevent the user from changing the step query parameters,
   * even if they are present."
   */
  useEffect(() => {
    const pageKeyExists = validKeysIndex;

    if (validKeysIndex === -1) {
      updateParamsPageIndex(preludePage);
    }
  }, []);

  /**
   * This function update encodedQueryParams and updateCurrentPageKey
   * @param {string} nextIndex
   * @returns {void}
   */
  const updateParamsPageIndex = nextIndex => {
    const nextKeyIndex = validKeys[nextIndex];

    if (nextKeyIndex) {
      const parsedNextKeyIndex = parseToKebabCase(nextKeyIndex);
      updateSearchParams('step', parsedNextKeyIndex);
      setCurrentPageKey(nextKeyIndex);
    }
  };

  const currentPage = useMemo(
    () => children.find(child => child.props.title === currentPageKey),
    [currentPageKey, children],
  );

  const currentPageIndex = validKeys.indexOf(currentPageKey);

  const goForward = () => {
    const currentIndex = validKeys.indexOf(currentPageKey);
    const nextIndex = currentIndex + 1;
    if (nextIndex < validKeys.length) {
      updateParamsPageIndex(nextIndex);
    }
  };

  const goBackward = () => {
    const currentIndex = validKeys.indexOf(currentPageKey);
    const nextIndex = currentIndex - 1;
    if (nextIndex >= 0) {
      updateParamsPageIndex(nextIndex);
    }
  };

  const reset = () => {
    setCurrentPageKey(validKeys[0]);
  };

  const goTo = key => {
    const nextIndex = validKeys.indexOf(key);

    if (validKeys.includes(key)) {
      updateParamsPageIndex(nextIndex);
    }
  };

  return {
    goForward,
    goBackward,
    goTo,
    reset,
    currentPage,
    validKeys,
    currentPageKey,
    currentPageIndex,
  };
}
